import { derived } from 'svelte/store';
import { configurationStore } from '../AppConfiguration';
import { formatInterval, formatMoney } from '../Utilities';
import type { StorefrontCurrency } from '../AppTypes';

/*
 * The enabled `Currency` rows `/api/configuration` ships, and the formatting built on them.
 *
 * The file kept its name from when this client also served the storefront, whose currency
 * selector, per-browser override and first-purchase pin lived here too. Those moved to AbstractID
 * with the customer pages (`Client/src/store/storefront.ts` there); what is left is what the admin
 * screens format money and offer currencies from.
 */

/**
 * The currency an admin screen prices in by default: the server's `suggestedCurrency`, else the
 * store default. There is no selector here — the storefront's override and pin were a
 * customer's choice, and an operator authoring the catalogue picks a currency per field instead.
 */
export const activeCurrencyCode = derived(configurationStore, ($configuration) => {
	const enabled = $configuration?.currencies ?? [];
	return $configuration?.suggestedCurrency ?? enabled.find(currency => currency.isDefault)?.code ?? '';
});

/**
 * The whole `Currency` row behind that code — the object `PriceDisplay` formats from. Undefined
 * while the configuration is still loading, which is a rendering state rather than an error.
 */
export const activeCurrency = derived(
	[activeCurrencyCode, configurationStore],
	([$code, $configuration]): StorefrontCurrency | undefined =>
		($configuration?.currencies ?? []).find(currency => currency.code === $code)
);

/** The currencies a picker may offer, in the display order the admin set. */
export const availableCurrencies = derived(
	configurationStore,
	($configuration): StorefrontCurrency[] =>
		[...($configuration?.currencies ?? [])].sort(
			(left, right) => left.displayOrder - right.displayOrder || left.code.localeCompare(right.code)
		)
);

/**
 * Formats a minor-unit amount whose currency arrives as a bare code.
 *
 * The admin sale screens work from server DTOs that carry `currency: 'jpy'` and an integer, never a
 * whole `Currency` row — so every figure on those pages needs the row looked up before it can be
 * formatted. Returning **null** when the row is missing is the point: a client that falls back to
 * ÷100 renders ¥1,200 as ¥12 and looks entirely plausible doing it, so the caller says the amount
 * is unavailable instead of quoting a wrong one.
 */
export const formatAmount = derived(configurationStore, ($configuration) => {
	const rows = $configuration?.currencies ?? [];
	return (amountMinor: number | null | undefined, code: string | null | undefined): string | null => {
		if (amountMinor === null || amountMinor === undefined) return null;
		const row = rows.find(currency => currency.code.toLowerCase() === (code ?? '').toLowerCase());
		return row === undefined ? null : formatMoney(amountMinor, row);
	};
});

/**
 * What a **held subscription** costs, said unambiguously (§9.7).
 *
 * On a per-seat plan the unit amount alone is actively misleading: "$20/month" beside a ten-seat
 * team states a figure that is nowhere near what they pay, and it is wrong in the direction that
 * looks cheap. So the total leads, and the working is shown behind it.
 *
 * Every screen that names the price of a subscription the customer already holds goes through
 * this, because the ambiguity is identical on all of them and a per-screen fix is how one of them
 * gets missed.
 */
export const subscriptionPriceLine = derived(formatAmount, ($formatAmount) => {
	return (subscription: {
		amount: number | null;
		total?: number | null;
		isSeatBased?: boolean;
		quantity?: number;
		currency: string;
		interval: string | null;
		intervalCount: number;
	}): string => {
		const period = formatInterval(subscription.interval, subscription.intervalCount);

		if (subscription.isSeatBased !== true) {
			const flat = $formatAmount(subscription.amount, subscription.currency);
			return flat === null ? '' : `${flat}${period}`;
		}

		const seats = subscription.quantity ?? 1;
		const total = $formatAmount(subscription.total ?? null, subscription.currency);
		const unit = $formatAmount(subscription.amount, subscription.currency);
		if (total === null) {
			return unit === null ? '' : `${unit}${period}/user`;
		}
		return unit === null
			? `${total}${period}`
			: `${total}${period} · ${seats} ${seats === 1 ? 'user' : 'users'} at ${unit} each`;
	};
});

/** The whole `Currency` row behind a bare code, for the components that format from a row. */
export const currencyRow = derived(
	configurationStore,
	($configuration) =>
		(code: string | null | undefined): StorefrontCurrency | undefined =>
			($configuration?.currencies ?? []).find(
				currency => currency.code.toLowerCase() === (code ?? '').toLowerCase()
			)
);

/**
 * Whole `Currency` rows for a list of bare codes, for the controls that format from a row.
 *
 * The analytics filters arrive as the codes that actually appear in the data, which is not the
 * same set as the currencies enabled today: a store that has since turned EUR off still has
 * months of euro sessions to filter by. So a code with no row is given a **minimal one built from
 * itself** rather than dropped — the picker then offers `EUR` under its own code instead of the
 * filter silently losing a slice of the store's history.
 */
export const currencyRowsOf = derived(
	currencyRow,
	($currencyRow) =>
		(codes: string[]): StorefrontCurrency[] =>
			codes.map(
				code =>
					$currencyRow(code) ?? {
						code,
						name: code.toUpperCase(),
						symbol: code.toUpperCase(),
						minorUnitDigits: 2,
						roundingIncrement: 1,
						displayOrder: 0,
						isDefault: false
					}
			)
);
